import { useQuery, useMutation, type QueryFunction, type UseQueryOptions, type UseMutationOptions } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";

export interface PayrollTaxConfig {
  id: string;
  companyId: string;
  name: string;
  type: string;
  rate: string;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface ListPayrollTaxConfigsParams {
  companyId?: string;
}

export interface CreatePayrollTaxConfigBody {
  id: string;
  companyId: string;
  name: string;
  type?: string;
  rate?: string;
  isActive?: boolean;
  order?: number;
}

export interface UpdatePayrollTaxConfigBody {
  name?: string;
  type?: string;
  rate?: string;
  isActive?: boolean;
  order?: number;
}

const getListPayrollTaxConfigsUrl = (params?: ListPayrollTaxConfigsParams) => {
  const query = params?.companyId ? `?companyId=${encodeURIComponent(params.companyId)}` : "";
  return `/api/payroll-tax-configs${query}`;
};

const listPayrollTaxConfigs = async (params?: ListPayrollTaxConfigsParams, options?: RequestInit): Promise<PayrollTaxConfig[]> =>
  customFetch<PayrollTaxConfig[]>(getListPayrollTaxConfigsUrl(params), { ...options, method: "GET" });

export const getListPayrollTaxConfigsQueryKey = (params?: ListPayrollTaxConfigsParams) =>
  [`/api/payroll-tax-configs`, ...(params ? [params] : [])] as const;

export function useListPayrollTaxConfigs<TData = PayrollTaxConfig[]>(
  params?: ListPayrollTaxConfigsParams,
  options?: Omit<UseQueryOptions<PayrollTaxConfig[], unknown, TData>, "queryKey" | "queryFn">,
) {
  return useQuery<PayrollTaxConfig[], unknown, TData>({
    queryKey: getListPayrollTaxConfigsQueryKey(params),
    queryFn: ({ signal }) => listPayrollTaxConfigs(params, { signal }),
    ...options,
  });
}

const createPayrollTaxConfig = async (body: CreatePayrollTaxConfigBody): Promise<PayrollTaxConfig> =>
  customFetch<PayrollTaxConfig>("/api/payroll-tax-configs", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

export function useCreatePayrollTaxConfig(
  options?: UseMutationOptions<PayrollTaxConfig, unknown, { data: CreatePayrollTaxConfigBody }>,
) {
  return useMutation<PayrollTaxConfig, unknown, { data: CreatePayrollTaxConfigBody }>({
    mutationFn: ({ data }) => createPayrollTaxConfig(data),
    ...options,
  });
}

const updatePayrollTaxConfig = async (id: string, body: UpdatePayrollTaxConfigBody): Promise<PayrollTaxConfig> =>
  customFetch<PayrollTaxConfig>(`/api/payroll-tax-configs/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

export function useUpdatePayrollTaxConfig(
  options?: UseMutationOptions<PayrollTaxConfig, unknown, { id: string; data: UpdatePayrollTaxConfigBody }>,
) {
  return useMutation<PayrollTaxConfig, unknown, { id: string; data: UpdatePayrollTaxConfigBody }>({
    mutationFn: ({ id, data }) => updatePayrollTaxConfig(id, data),
    ...options,
  });
}

const deletePayrollTaxConfig = async (id: string): Promise<void> =>
  customFetch<void>(`/api/payroll-tax-configs/${id}`, { method: "DELETE" });

export function useDeletePayrollTaxConfig(
  options?: UseMutationOptions<void, unknown, { id: string }>,
) {
  return useMutation<void, unknown, { id: string }>({
    mutationFn: ({ id }) => deletePayrollTaxConfig(id),
    ...options,
  });
}
